import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, LucideLogOut, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../LanguageSelector';
import { buildMobileNavigationItems } from './mobileNavigationItems';

const sectionLabels = {
  account: 'myAccount',
  preferences: 'preferences',
  administration: 'administration',
  system: 'systemAdministration'
};

const sectionOrder = ['account', 'preferences', 'administration', 'system'];

const isActivePath = (pathname, path) => {
  if (!path) return false;
  if (path === '/') return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
};

function MobileBottomNavigation({
  userRole,
  showNotificationMenu = false,
  showNotificationManagementMenu = false,
  showSurveyMenu = false,
  showUserSurveyMenu = false,
  showSurveyBadge = false,
  onLogout,
  onSurveyClick
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const sheetRef = useRef(null);
  const moreTriggerRef = useRef(null);
  const [isMoreOpen, setMoreOpen] = useState(false);

  const closeMoreAndRestoreFocus = useCallback(() => {
    setMoreOpen(false);
    requestAnimationFrame(() => moreTriggerRef.current?.focus());
  }, []);

  const { tabs, moreItems } = useMemo(
    () =>
      buildMobileNavigationItems({
        userRole,
        t,
        showNotificationMenu,
        showNotificationManagementMenu,
        showSurveyMenu,
        showUserSurveyMenu
      }),
    [
      showNotificationManagementMenu,
      showNotificationMenu,
      showSurveyMenu,
      showUserSurveyMenu,
      t,
      userRole
    ]
  );

  const groupedMoreItems = useMemo(
    () =>
      moreItems.reduce((groups, item) => {
        const section = item.section || 'account';
        return {
          ...groups,
          [section]: [...(groups[section] || []), item]
        };
      }, {}),
    [moreItems]
  );

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;

    const bodyStyle = document.body.style;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previousBodyStyles = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      width: bodyStyle.width,
      overflowY: bodyStyle.overflowY
    };

    /* Fixing the body at the inverse scroll offset prevents gestures on the
       exposed backdrop from moving the page while preserving its visual
       position. Cleanup restores exact prior inline values, including values
       owned by another feature, and runs for every close path and unmount. */
    bodyStyle.position = 'fixed';
    bodyStyle.top = `${-scrollY}px`;
    bodyStyle.left = `${-scrollX}px`;
    bodyStyle.width = '100%';
    bodyStyle.overflowY = 'hidden';

    return () => {
      Object.assign(bodyStyle, previousBodyStyles);
      window.scrollTo(scrollX, scrollY);
    };
  }, [isMoreOpen]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMoreAndRestoreFocus();
        return;
      }

      if (event.key !== 'Tab' || !sheetRef.current) return;

      const focusableElements = sheetRef.current.querySelectorAll(
        'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    requestAnimationFrame(() => {
      const firstFocusable = sheetRef.current?.querySelector('a[href], button:not([disabled]), select:not([disabled])');
      firstFocusable?.focus();
    });

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMoreAndRestoreFocus, isMoreOpen]);

  const handleMoreItemClick = (item) => {
    if (item.id === 'feedback-surveys' && onSurveyClick) {
      onSurveyClick();
    }
    setMoreOpen(false);
  };

  const handleLogoutClick = (event) => {
    setMoreOpen(false);
    if (onLogout) {
      onLogout(event);
    }
  };

  return (
    <>
      {isMoreOpen && (
        <>
          <div
            data-testid="mobile-more-backdrop"
            className="mobile-more-backdrop fixed inset-0 z-40 bg-black/40 md:hidden"
            aria-hidden="true"
            onClick={closeMoreAndRestoreFocus}
          />
          <section
            ref={sheetRef}
            id="mobile-more-menu"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
            className="mobile-more-sheet fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-lg border-t border-gray-200 bg-white text-gray-900 shadow-[0_-8px_24px_rgba(15,23,42,0.18)] md:hidden dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          >
            <div className="mobile-more-header flex shrink-0 items-center justify-between border-b border-gray-200 px-4 pb-3 dark:border-gray-700">
              <h2 id="mobile-more-title" className="text-base font-semibold">
                {t('more')}
              </h2>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                onClick={closeMoreAndRestoreFocus}
                aria-label={t('close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div
              data-testid="mobile-more-scroll-body"
              className="mobile-more-scroll-body min-h-0 flex-auto overflow-y-auto px-4 pt-3"
            >
              {sectionOrder.map((section) => {
                const items = groupedMoreItems[section] || [];
                if (items.length === 0) return null;

                return (
                  <section className="mb-4" key={section}>
                    <h3 className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t(sectionLabels[section])}
                    </h3>
                    <div className="space-y-1">
                      {items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.id}
                            to={item.path}
                            className="flex min-h-11 items-center justify-between rounded-md px-3 py-3 text-sm transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-800"
                            onClick={() => handleMoreItemClick(item)}
                          >
                            <span className="flex min-w-0 items-center">
                              <Icon className="mr-3 h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
                              <span className="truncate">{item.label}</span>
                              {item.id === 'feedback-surveys' && showSurveyBadge && (
                                <span className="ml-2 h-2.5 w-2.5 rounded-full bg-blue-500" />
                              )}
                            </span>
                            <ChevronRight className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                );
              })}

              <section className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <div className="mb-3 rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700">
                  <LanguageSelector onLanguageChange={() => setMoreOpen(false)} />
                </div>
                <button
                  type="button"
                  className="flex min-h-11 w-full items-center rounded-md px-3 py-3 text-left text-sm transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-800"
                  onClick={handleLogoutClick}
                >
                  <LucideLogOut className="mr-3 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  {t('logout')}
                </button>
              </section>
            </div>
          </section>
        </>
      )}

      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex min-h-16 items-start border-t border-gray-200 bg-white px-2 pt-1 shadow-[0_-4px_14px_rgba(15,23,42,0.10)] md:hidden dark:border-gray-700 dark:bg-gray-900"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
        aria-label={t('menu')}
      >
        {tabs.map((item) => {
          const Icon = item.icon;
          const isMore = item.id === 'more';
          const isActive = isMore ? isMoreOpen : isActivePath(location.pathname, item.path);
          const baseClass = [
            'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-2 text-[11px] font-medium leading-tight transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500',
            isActive
              ? 'text-blue-700 dark:text-blue-300'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
          ].join(' ');
          const content = (
            <>
              <Icon className="h-5 w-5 shrink-0" />
              <span className="max-w-full truncate">{item.label}</span>
            </>
          );

          if (isMore) {
            return (
              <button
                ref={moreTriggerRef}
                key={item.id}
                type="button"
                className={baseClass}
                aria-controls="mobile-more-menu"
                aria-expanded={isMoreOpen}
                onClick={() => setMoreOpen((open) => !open)}
              >
                {content}
              </button>
            );
          }

          return (
            <Link key={item.id} to={item.path} className={baseClass}>
              {content}
            </Link>
          );
        })}
      </nav>
    </>
  );
}

export default MobileBottomNavigation;
